import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import api from '../../services/api';
import { setUiPayload } from '../canvas/canvasSlice';
import { setOrderState } from '../order/orderSlice';
import { nanoid } from 'nanoid';

// Define a type for the message
// Define a type for the message content
export type MessageContent = string | { userFacingMessage: string; internalMessage: any };

export interface Message {
  id: string;
  author: 'user' | 'ai';
  content: MessageContent;
  tool_calls?: any;
  uiPayload?: Record<string, any>;
  suggestedActions?: string[];
}

// Define a type for the slice state
interface ChatState {
  messages: Message[];
  isLoading: boolean;
}

// Define the initial state using that type
const initialState: ChatState = {
  messages: [],
  isLoading: false,
};

export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async (messageContent: MessageContent, { getState, dispatch }) => {
    // Add user message to the chat history for immediate UI update.
    // The initial empty message should not be added to the history.
    if (messageContent !== '') {
      dispatch(addMessage({ author: 'user', content: messageContent }));
    }


    // Determine the content to be sent to the backend.
    const contentForBackend = typeof messageContent === 'string' ? messageContent : messageContent.internalMessage;

    const state = getState() as RootState;
    const { user, temporaryUserId } = state.auth;
    const userId = user?.id;
    const isAuthenticated = !!user;
    const orderState = state.order;



    const response = await api.post('/chat', {
      messages: [{ role: 'user', content: JSON.stringify(contentForBackend) }],
      isAuthenticated,
      userId,
      temporaryUserId,
      orderState, // Pass the order state fetched from the store
    });


    const { aiMessage, uiPayload, updatedState } = response.data;
    
    // Extract suggestedActions from aiMessage if they exist
    const suggestedActions = aiMessage.suggestedActions;

    const aiMessageToAdd = {
      author: 'ai' as const,
      content: aiMessage.content,
      tool_calls: aiMessage.tool_calls,
      uiPayload,
      suggestedActions,
    };

    dispatch(addMessage(aiMessageToAdd));

    if (uiPayload) {
      dispatch(setUiPayload(uiPayload));
    }

    // If the AI response contains an updated order state, dispatch it
    if (updatedState && Object.keys(updatedState).length > 0) {
      dispatch(setOrderState(updatedState));
    }



    return response.data;
  }
);

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<Omit<Message, 'id'>>) => {
      state.messages.push({ id: nanoid(), ...action.payload });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendMessage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sendMessage.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(sendMessage.rejected, (state) => {
        state.isLoading = false;
        // Optionally add an error message to the chat
        state.messages.push({
          id: new Date().toISOString(),
          author: 'ai',
          content: 'Sorry, I encountered an error. Please try again.',
        });
      });
  },
});

export const { addMessage } = chatSlice.actions;

export const selectChatMessages = (state: RootState) => state.chat.messages;
export const selectIsChatLoading = (state: RootState) => state.chat.isLoading;



export default chatSlice.reducer;
